import {
  AnimatePresence,
  motion,
  useAnimationFrame,
  useInView,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue
} from 'framer-motion';
import {
  Braces,
  Database,
  FileStack,
  Pause,
  Play,
  RadioTower,
  ServerCog,
  type LucideIcon
} from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type PointerEvent as ReactPointerEvent
} from 'react';

import type { PlatformCapability } from '../HomeData';

type StageIndex = 0 | 1;
type Point = readonly [number, number];
type HoveredNode = string | null;
type AnimationClock = MotionValue<number>;

type PipelineLayout = {
  compact: boolean;
  contract: Point;
  height: number;
  landing: Point;
  processing: readonly [Point, Point, Point, Point];
  sources: readonly [Point, Point, Point, Point];
  validation: Point;
  width: number;
};

const PIPELINE_TIMING = {
  camera: 1.05,
  loop: 5.5,
  manualPause: 7,
  packet: 4.2
} as const;

const EASE = [0.22, 1, 0.36, 1] as const;

const DESKTOP_LAYOUT: PipelineLayout = {
  compact: false,
  contract: [244, 126],
  height: 252,
  landing: [574, 126],
  processing: [
    [94, 126],
    [278, 126],
    [466, 126],
    [640, 126]
  ],
  sources: [
    [66, 45],
    [47, 101],
    [76, 164],
    [142, 211]
  ],
  validation: [374, 126],
  width: 720
};

const COMPACT_LAYOUT: PipelineLayout = {
  compact: true,
  contract: [180, 126],
  height: 252,
  landing: [388, 126],
  processing: [
    [57, 126],
    [168, 126],
    [283, 126],
    [384, 126]
  ],
  sources: [
    [36, 43],
    [31, 99],
    [41, 163],
    [92, 211]
  ],
  validation: [278, 126],
  width: 460
};

const SOURCE_NODES: ReadonlyArray<{
  icon: LucideIcon;
  id: string;
  label: string;
  packet: string;
}> = [
  { icon: Database, id: 'database', label: 'Database', packet: 'CDC' },
  { icon: ServerCog, id: 'api', label: 'API', packet: 'JSON' },
  { icon: RadioTower, id: 'events', label: 'Event stream', packet: 'Event' },
  { icon: FileStack, id: 'files', label: 'Files', packet: 'CSV' }
];

const classNames = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const clamp = (value: number, min = 0, max = 1) =>
  Math.min(max, Math.max(min, value));

const lerp = (start: number, end: number, progress: number) =>
  start + (end - start) * progress;

const wrapSeconds = (seconds: number, duration: number) =>
  ((seconds % duration) + duration) % duration;

const getCycleProgress = (
  seconds: number,
  delay: number,
  duration: number,
  repeatDelay = 0
) => {
  const cycleDuration = duration + repeatDelay;
  const elapsed = wrapSeconds(seconds - delay, cycleDuration);

  if (elapsed > duration) return null;

  return elapsed / duration;
};

const getTriangleProgress = (progress: number) =>
  progress <= 0.5 ? progress * 2 : (1 - progress) * 2;

const getPathPoint = (points: readonly Point[], progress: number): Point => {
  if (points.length === 1) return points[0];

  const clampedProgress = clamp(progress);
  const scaledProgress = clampedProgress * (points.length - 1);
  const segmentIndex = Math.min(points.length - 2, Math.floor(scaledProgress));
  const segmentProgress = scaledProgress - segmentIndex;
  const start = points[segmentIndex];
  const end = points[segmentIndex + 1];

  return [
    lerp(start[0], end[0], segmentProgress),
    lerp(start[1], end[1], segmentProgress)
  ];
};

const getPacketOpacity = (progress: number | null) => {
  if (progress === null) return 0;
  if (progress < 0.08) return progress / 0.08;
  if (progress < 0.9) return 1;

  return clamp(1 - (progress - 0.9) / 0.1);
};

const getPulseOpacity = (progress: number | null, peak = 1) => {
  if (progress === null) return 0;

  return peak * getTriangleProgress(progress);
};

const usePausableClock = (isEnabled: boolean, isPaused: boolean) => {
  const clock = useMotionValue(0);
  const elapsedRef = useRef(0);
  const previousFrameRef = useRef<number | null>(null);

  useAnimationFrame(time => {
    if (!isEnabled || isPaused) {
      previousFrameRef.current = time;
      return;
    }

    if (previousFrameRef.current === null) {
      previousFrameRef.current = time;
      return;
    }

    elapsedRef.current += time - previousFrameRef.current;
    previousFrameRef.current = time;
    clock.set(elapsedRef.current);
  });

  useEffect(() => {
    if (!isEnabled) {
      previousFrameRef.current = null;
    }
  }, [isEnabled]);

  return clock;
};

const useCompactViewport = () => {
  const [isCompact, setIsCompact] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(max-width: 639px)').matches
  );

  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const update = () => setIsCompact(query.matches);

    update();
    query.addEventListener('change', update);

    return () => query.removeEventListener('change', update);
  }, []);

  return isCompact;
};

const getNodeOpacity = (hoveredNode: HoveredNode, nodeId: string) => {
  if (!hoveredNode) return 1;
  return hoveredNode === nodeId ? 1 : 0.34;
};

const getPathOpacity = (
  hoveredNode: HoveredNode,
  connectedNodes: readonly string[]
) => {
  if (!hoveredNode) return 0.34;
  return connectedNodes.includes(hoveredNode) ? 0.92 : 0.08;
};

const AnimatedConnector = ({
  clock,
  connectedNodes,
  d,
  hoveredNode,
  isAnimated,
  success = false
}: {
  clock: AnimationClock;
  connectedNodes: readonly string[];
  d: string;
  hoveredNode: HoveredNode;
  isAnimated: boolean;
  success?: boolean;
}) => {
  const strokeDashoffset = useTransform(clock, time =>
    isAnimated ? -wrapSeconds(time / 1000, 1.8) * (24 / 1.8) : 0
  );

  return (
    <>
      <motion.path
        d={d}
        fill="none"
        stroke={
          success
            ? 'var(--color-accent-success)'
            : 'var(--color-border-primary)'
        }
        strokeLinecap="round"
        strokeWidth="1.25"
        vectorEffect="non-scaling-stroke"
        animate={{ opacity: getPathOpacity(hoveredNode, connectedNodes) }}
        transition={{ duration: 0.25 }}
      />
      <motion.path
        d={d}
        fill="none"
        stroke={
          success
            ? 'var(--color-accent-success)'
            : 'var(--color-accent-primary)'
        }
        strokeDasharray="3 9"
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        strokeWidth="1.3"
        vectorEffect="non-scaling-stroke"
        animate={{ opacity: getPathOpacity(hoveredNode, connectedNodes) }}
        transition={{ duration: 0.25 }}
      />
    </>
  );
};

const NodeTooltip = ({
  label,
  width = 76,
  y
}: {
  label: string;
  width?: number;
  y: number;
}) => (
  <motion.g
    initial={{ opacity: 0, y: 3 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 3 }}
    transition={{ duration: 0.18 }}
    pointerEvents="none"
  >
    <rect
      x={-width / 2}
      y={y}
      width={width}
      height="20"
      rx="6"
      fill="var(--color-background-primary)"
      stroke="var(--color-border-primary)"
      vectorEffect="non-scaling-stroke"
    />
    <text
      x="0"
      y={y + 13.3}
      fill="var(--color-text-secondary)"
      fontFamily="var(--font-mono)"
      fontSize="9"
      textAnchor="middle"
    >
      {label}
    </text>
  </motion.g>
);

const SourceNode = ({
  compact,
  hoveredNode,
  icon: Icon,
  id,
  label,
  onHoverChange,
  packet,
  point
}: {
  compact: boolean;
  hoveredNode: HoveredNode;
  icon: LucideIcon;
  id: string;
  label: string;
  onHoverChange: (node: HoveredNode) => void;
  packet: string;
  point: Point;
}) => {
  const isHovered = hoveredNode === id;

  return (
    <g transform={`translate(${point[0]} ${point[1]})`}>
      <motion.g
        role="img"
        aria-label={`${label} source, ${packet} packets`}
        tabIndex={0}
        className="typical-path-node cursor-default outline-none"
        animate={{
          opacity: getNodeOpacity(hoveredNode, id),
          scale: isHovered ? 1.06 : 1
        }}
        transition={{ duration: 0.24, ease: EASE }}
        onFocus={() => onHoverChange(id)}
        onBlur={() => onHoverChange(null)}
        onPointerEnter={() => onHoverChange(id)}
        onPointerLeave={() => onHoverChange(null)}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <title>{`${label}: generating ${packet} packets`}</title>
        <circle
          className="typical-path-node__surface"
          cx="0"
          cy="0"
          r="18"
          vectorEffect="non-scaling-stroke"
        />
        <circle cx="0" cy="0" r="13" fill="var(--color-background-secondary)" />
        <Icon
          aria-hidden="true"
          x="-7"
          y="-7"
          width="14"
          height="14"
          color="var(--color-accent-primary)"
          strokeWidth="1.65"
        />
        <text
          x="25"
          y="4"
          fill="var(--color-text-secondary)"
          fontFamily="var(--font-mono)"
          fontSize={compact ? '13' : '10'}
        >
          {label}
        </text>
        <AnimatePresence>
          {isHovered && <NodeTooltip label={`${packet} packets`} y={-34} />}
        </AnimatePresence>
      </motion.g>
    </g>
  );
};

const DataPacket = ({
  clock,
  delay,
  duration = PIPELINE_TIMING.packet,
  isAnimated,
  label,
  points,
  repeatDelay = 0.65,
  success = false
}: {
  clock: AnimationClock;
  delay: number;
  duration?: number;
  isAnimated: boolean;
  label: string;
  points: readonly Point[];
  repeatDelay?: number;
  success?: boolean;
}) => {
  const progress = useTransform(clock, time =>
    isAnimated
      ? getCycleProgress(time / 1000, delay, duration, repeatDelay)
      : null
  );
  const x = useTransform(progress, currentProgress =>
    currentProgress === null
      ? points[0][0]
      : getPathPoint(points, currentProgress)[0]
  );
  const y = useTransform(progress, currentProgress =>
    currentProgress === null
      ? points[0][1]
      : getPathPoint(points, currentProgress)[1]
  );
  const opacity = useTransform(progress, getPacketOpacity);

  return (
    <motion.g
      aria-hidden="true"
      style={{
        opacity,
        x,
        y
      }}
    >
      <rect
        x="-4"
        y="-3"
        width="8"
        height="6"
        rx="2"
        fill={
          success
            ? 'var(--color-accent-success)'
            : 'var(--color-accent-primary)'
        }
      />
      <rect
        x="-7"
        y="-5"
        width="14"
        height="10"
        rx="4"
        fill="none"
        stroke={
          success
            ? 'var(--color-accent-success)'
            : 'var(--color-accent-primary)'
        }
        strokeOpacity="0.24"
        vectorEffect="non-scaling-stroke"
      />
      <title>{label}</title>
    </motion.g>
  );
};

const ContractGate = ({
  compact,
  hoveredNode,
  label,
  onHoverChange,
  point
}: {
  compact: boolean;
  hoveredNode: HoveredNode;
  label: string;
  onHoverChange: (node: HoveredNode) => void;
  point: Point;
}) => {
  const id = 'contracts';
  const isHovered = hoveredNode === id;

  return (
    <g transform={`translate(${point[0]} ${point[1]})`}>
      <motion.g
        role="img"
        aria-label={`${label}: schema gate`}
        tabIndex={0}
        className="typical-path-node cursor-default outline-none"
        animate={{
          opacity: getNodeOpacity(hoveredNode, id),
          scale: isHovered ? 1.06 : 1
        }}
        transition={{ duration: 0.24, ease: EASE }}
        onFocus={() => onHoverChange(id)}
        onBlur={() => onHoverChange(null)}
        onPointerEnter={() => onHoverChange(id)}
        onPointerLeave={() => onHoverChange(null)}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <title>{`${label}: standardizing packets against a schema`}</title>
        <path
          d="M-25 -36 H-34 V36 H-25 M25 -36 H34 V36 H25"
          fill="none"
          stroke="var(--color-accent-primary)"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
        <rect
          className="typical-path-node__surface"
          x="-22"
          y="-29"
          width="44"
          height="58"
          rx="12"
          vectorEffect="non-scaling-stroke"
        />
        <Braces
          aria-hidden="true"
          x="-9"
          y="-9"
          width="18"
          height="18"
          color="var(--color-accent-primary)"
          strokeWidth="1.5"
        />
        <text
          x="0"
          y="51"
          fill="var(--color-text-primary)"
          fontFamily="var(--font-mono)"
          fontSize={compact ? '13' : '10'}
          textAnchor="middle"
        >
          {label}
        </text>
        <AnimatePresence>
          {isHovered && <NodeTooltip label="Schema match" y={-55} />}
        </AnimatePresence>
      </motion.g>
    </g>
  );
};

const ValidationGate = ({
  clock,
  compact,
  hoveredNode,
  isAnimated,
  label,
  onHoverChange,
  point
}: {
  clock: AnimationClock;
  compact: boolean;
  hoveredNode: HoveredNode;
  isAnimated: boolean;
  label: string;
  onHoverChange: (node: HoveredNode) => void;
  point: Point;
}) => {
  const id = 'validation';
  const isHovered = hoveredNode === id;
  const pulseProgress = useTransform(clock, time =>
    isAnimated ? getCycleProgress(time / 1000, 1.65, 1.6, 2.5) : null
  );
  const pulseOpacity = useTransform(pulseProgress, progress =>
    getPulseOpacity(progress, 0.42)
  );
  const pulseScale = useTransform(pulseProgress, progress =>
    progress === null ? 1 : lerp(0.74, 1.38, progress)
  );

  return (
    <g transform={`translate(${point[0]} ${point[1]})`}>
      <motion.circle
        aria-hidden="true"
        cx="0"
        cy="0"
        r="31"
        fill="none"
        stroke="var(--color-accent-success)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
        style={{
          opacity: pulseOpacity,
          scale: pulseScale,
          transformBox: 'fill-box',
          transformOrigin: 'center'
        }}
      />
      <motion.g
        role="img"
        aria-label={`${label}: quality check`}
        tabIndex={0}
        className="typical-path-node cursor-default outline-none"
        animate={{
          opacity: getNodeOpacity(hoveredNode, id),
          scale: isHovered ? 1.06 : 1
        }}
        transition={{ duration: 0.24, ease: EASE }}
        onFocus={() => onHoverChange(id)}
        onBlur={() => onHoverChange(null)}
        onPointerEnter={() => onHoverChange(id)}
        onPointerLeave={() => onHoverChange(null)}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <title>{`${label}: valid packets continue; incomplete packets divert`}</title>
        <circle
          className="typical-path-node__surface"
          cx="0"
          cy="0"
          r="28"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M-9 0 L-2 7 L11 -8"
          fill="none"
          stroke="var(--color-accent-success)"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        <text
          x="0"
          y="48"
          fill="var(--color-text-primary)"
          fontFamily="var(--font-mono)"
          fontSize={compact ? '13' : '10'}
          textAnchor="middle"
        >
          {label}
        </text>
        <AnimatePresence>
          {isHovered && <NodeTooltip label="Quality passed" y={-52} />}
        </AnimatePresence>
      </motion.g>
    </g>
  );
};

const LandingZone = ({
  compact,
  hoveredNode,
  label,
  onHoverChange,
  point
}: {
  compact: boolean;
  hoveredNode: HoveredNode;
  label: string;
  onHoverChange: (node: HoveredNode) => void;
  point: Point;
}) => {
  const id = 'landing';
  const isHovered = hoveredNode === id;

  return (
    <g transform={`translate(${point[0]} ${point[1]})`}>
      <motion.g
        role="img"
        aria-label={`${label}: organized dataset`}
        tabIndex={0}
        className="typical-path-node cursor-default outline-none"
        animate={{
          opacity: getNodeOpacity(hoveredNode, id),
          scale: isHovered ? 1.045 : 1
        }}
        transition={{ duration: 0.24, ease: EASE }}
        onFocus={() => onHoverChange(id)}
        onBlur={() => onHoverChange(null)}
        onPointerEnter={() => onHoverChange(id)}
        onPointerLeave={() => onHoverChange(null)}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <title>{`${label}: accepted packets assembled into one dataset`}</title>
        <path
          d="M-38 -36 H38 V35 H-38 Z M-30 -26 H30 M-30 24 H30"
          fill="var(--color-background-secondary)"
          stroke="var(--color-border-primary)"
          strokeWidth="1.15"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d="M-38 -36 H-14 L-8 -29 H38"
          fill="none"
          stroke="var(--color-accent-primary)"
          strokeWidth="1.4"
          vectorEffect="non-scaling-stroke"
        />
        <text
          x="0"
          y="52"
          fill="var(--color-text-primary)"
          fontFamily="var(--font-mono)"
          fontSize={compact ? '13' : '10'}
          textAnchor="middle"
        >
          {label}
        </text>
        <AnimatePresence>
          {isHovered && <NodeTooltip label="Dataset ready" y={-57} />}
        </AnimatePresence>
      </motion.g>
    </g>
  );
};

const SharedDataset = ({
  activeStage,
  layout,
  reduceMotion
}: {
  activeStage: StageIndex;
  layout: PipelineLayout;
  reduceMotion: boolean;
}) => {
  return (
    <g transform={`translate(${layout.landing[0]} ${layout.landing[1]})`}>
      <motion.g
        aria-hidden="true"
        initial={false}
        animate={
          activeStage === 0
            ? { opacity: [0, 0.25, 1], scale: 1, x: 0, y: 0 }
            : { opacity: 0, scale: 0.96, x: 0, y: 0 }
        }
        transition={{
          duration: reduceMotion || activeStage === 1 ? 0.2 : 0.72,
          ease: EASE,
          opacity:
            activeStage === 0
              ? {
                  duration: reduceMotion ? 0.15 : 0.72,
                  times: [0, 0.35, 1]
                }
              : { duration: 0.16 }
        }}
      >
        {[-14, -5, 4, 13].map((y, index) => (
          <motion.rect
            key={y}
            x="-25"
            y={y}
            width={index % 2 === 0 ? 49 : 42}
            height="5"
            rx="2.5"
            fill={
              index === 3
                ? 'var(--color-accent-success)'
                : 'var(--color-accent-primary)'
            }
            fillOpacity={index === 3 ? 0.85 : 0.64}
            initial={false}
            animate={
              activeStage === 0
                ? { scaleX: [0.35, 1], x: [10, 0] }
                : { scaleX: 1, x: 0 }
            }
            transition={{
              delay: reduceMotion ? 0 : index * 0.1,
              duration: reduceMotion ? 0.2 : 0.55,
              ease: EASE
            }}
            style={{ transformBox: 'fill-box', transformOrigin: 'left' }}
          />
        ))}
      </motion.g>
    </g>
  );
};

const HoldPulse = ({
  clock,
  isAnimated,
  point
}: {
  clock: AnimationClock;
  isAnimated: boolean;
  point: Point;
}) => {
  const opacity = useTransform(clock, time =>
    isAnimated
      ? getPulseOpacity(getCycleProgress(time / 1000, 2.2, 1.4, 2.6), 0.75)
      : 0
  );

  return (
    <motion.circle
      aria-hidden="true"
      cx={point[0]}
      cy={point[1]}
      r="3"
      fill="var(--color-accent-secondary)"
      opacity={opacity}
    />
  );
};

const DataIngestionScene = ({
  active,
  clock,
  hoveredNode,
  isAnimated,
  layout,
  onHoverChange,
  route
}: {
  active: boolean;
  clock: AnimationClock;
  hoveredNode: HoveredNode;
  isAnimated: boolean;
  layout: PipelineLayout;
  onHoverChange: (node: HoveredNode) => void;
  route: readonly string[];
}) => {
  const [contractX, contractY] = layout.contract;
  const [validationX, validationY] = layout.validation;
  const [landingX, landingY] = layout.landing;
  const contractEntryX = contractX - 34;
  const contractExitX = contractX + 34;
  const validationExitX = validationX + 28;
  const landingEntryX = landingX - 38;

  return (
    <motion.g
      initial={false}
      animate={{ opacity: active ? 1 : 0.34 }}
      transition={{ duration: 0.4 }}
    >
      <text
        x="18"
        y="20"
        fill="var(--color-text-muted)"
        fontFamily="var(--font-mono)"
        fontSize={layout.compact ? '11' : '8.5'}
        letterSpacing="1.3"
      >
        04 APPROVED SOURCES
      </text>

      {layout.sources.map((source, index) => {
        const sourceNode = SOURCE_NODES[index];
        const d = `M${source[0] + 18} ${source[1]} C${source[0] + 82} ${source[1]} ${contractEntryX - 56} ${contractY} ${contractEntryX} ${contractY}`;

        return (
          <AnimatedConnector
            key={sourceNode.id}
            clock={clock}
            d={d}
            connectedNodes={[sourceNode.id, 'contracts']}
            hoveredNode={hoveredNode}
            isAnimated={isAnimated && active}
          />
        );
      })}

      <AnimatedConnector
        clock={clock}
        d={`M${contractExitX} ${contractY} C${contractExitX + 35} ${contractY} ${validationX - 52} ${validationY} ${validationX - 28} ${validationY}`}
        connectedNodes={['contracts', 'validation']}
        hoveredNode={hoveredNode}
        isAnimated={isAnimated && active}
      />
      <AnimatedConnector
        clock={clock}
        d={`M${validationExitX} ${validationY} C${validationExitX + 44} ${validationY} ${landingEntryX - 38} ${landingY} ${landingEntryX} ${landingY}`}
        connectedNodes={['validation', 'landing']}
        hoveredNode={hoveredNode}
        isAnimated={isAnimated && active}
        success
      />

      <motion.path
        aria-hidden="true"
        d={`M${validationX} ${validationY + 28} C${validationX} ${validationY + 54} ${validationX + 38} ${validationY + 55} ${validationX + 52} ${validationY + 71}`}
        fill="none"
        stroke="var(--color-accent-secondary)"
        strokeDasharray="2 5"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
        animate={{ opacity: hoveredNode === 'validation' ? 0.72 : 0.28 }}
      />
      <g aria-hidden="true">
        <circle
          cx={validationX + 54}
          cy={validationY + 73}
          r="3"
          fill="var(--color-accent-secondary)"
          fillOpacity="0.7"
        />
        <text
          x={validationX + 62}
          y={validationY + 76}
          fill="var(--color-text-muted)"
          fontFamily="var(--font-mono)"
          fontSize="8"
        >
          HOLD
        </text>
      </g>

      {SOURCE_NODES.map((source, index) => (
        <SourceNode
          key={source.id}
          {...source}
          compact={layout.compact}
          point={layout.sources[index]}
          hoveredNode={hoveredNode}
          onHoverChange={onHoverChange}
        />
      ))}

      <ContractGate
        compact={layout.compact}
        point={layout.contract}
        label={route[1] ?? 'Contracts'}
        hoveredNode={hoveredNode}
        onHoverChange={onHoverChange}
      />
      <ValidationGate
        clock={clock}
        compact={layout.compact}
        point={layout.validation}
        label={route[2] ?? 'Validation'}
        hoveredNode={hoveredNode}
        isAnimated={isAnimated && active}
        onHoverChange={onHoverChange}
      />
      <LandingZone
        compact={layout.compact}
        point={layout.landing}
        label={route[3] ?? 'Landing zone'}
        hoveredNode={hoveredNode}
        onHoverChange={onHoverChange}
      />

      {isAnimated &&
        active &&
        layout.sources.map((source, index) => {
          const sourceNode = SOURCE_NODES[index];

          return (
            <DataPacket
              key={sourceNode.id}
              clock={clock}
              label={`${sourceNode.packet} packet`}
              delay={index * 0.48}
              isAnimated={isAnimated && active}
              points={[
                [source[0] + 18, source[1]],
                [(source[0] + contractEntryX) / 2, (source[1] + contractY) / 2],
                [contractX, contractY],
                [validationX, validationY],
                [landingX - 12, landingY]
              ]}
              success
            />
          );
        })}

      {isAnimated && active && (
        <HoldPulse
          clock={clock}
          isAnimated={isAnimated && active}
          point={[validationX + 54, validationY + 73]}
        />
      )}
    </motion.g>
  );
};

type OperationKind = 'validate' | 'transform' | 'enrich' | 'publish';

const ValidateGlyph = ({
  clock,
  isAnimated
}: {
  clock: AnimationClock;
  isAnimated: boolean;
}) => {
  const scanY = useTransform(clock, time => {
    if (!isAnimated) return 0;

    const progress = wrapSeconds(time / 1000, 3.6) / 3.6;
    return lerp(-16, 16, getTriangleProgress(progress));
  });

  return (
    <>
      <circle
        r="29"
        fill="var(--color-background-card)"
        stroke="var(--color-border-primary)"
        vectorEffect="non-scaling-stroke"
      />
      {[-9, 0, 9].map((y, index) => (
        <rect
          key={y}
          x="-14"
          y={y - 2}
          width={index === 1 ? 23 : 28}
          height="4"
          rx="2"
          fill="var(--color-accent-primary)"
          fillOpacity={index === 1 ? 0.42 : 0.7}
        />
      ))}
      <motion.line
        x1="-19"
        x2="19"
        y1={scanY}
        y2={scanY}
        stroke="var(--color-accent-success)"
        strokeWidth="1.4"
        vectorEffect="non-scaling-stroke"
      />
    </>
  );
};

const TransformBlock = ({
  clock,
  index,
  isAnimated,
  x,
  y
}: {
  clock: AnimationClock;
  index: number;
  isAnimated: boolean;
  x: number;
  y: number;
}) => {
  const blockX = useTransform(clock, time => {
    if (!isAnimated) return x;

    const progress = getCycleProgress(time / 1000, 0.18 * index, 2.4) ?? 0;
    const offset = getTriangleProgress(progress) * 14;

    return index % 2 === 0 ? x + offset : x - offset;
  });
  const blockY = useTransform(clock, time => {
    if (!isAnimated) return y;

    const progress = getCycleProgress(time / 1000, 0.18 * index, 2.4) ?? 0;
    const offset = getTriangleProgress(progress) * 14;

    return index < 2 ? y + offset : y - offset;
  });

  return (
    <motion.rect
      x={blockX}
      y={blockY}
      width="9"
      height="9"
      rx="2"
      fill="var(--color-accent-primary)"
      fillOpacity={0.42 + index * 0.12}
    />
  );
};

const TransformGlyph = ({
  clock,
  isAnimated
}: {
  clock: AnimationClock;
  isAnimated: boolean;
}) => (
  <>
    <path
      d="M0 -33 L33 0 L0 33 L-33 0 Z"
      fill="var(--color-background-card)"
      stroke="var(--color-border-primary)"
      vectorEffect="non-scaling-stroke"
    />
    {[
      [-11, -11],
      [3, -11],
      [-11, 3],
      [3, 3]
    ].map(([x, y], index) => (
      <TransformBlock
        key={`${x}-${y}`}
        clock={clock}
        index={index}
        isAnimated={isAnimated}
        x={x}
        y={y}
      />
    ))}
  </>
);

const EnrichDot = ({
  clock,
  index,
  isAnimated,
  x,
  y
}: {
  clock: AnimationClock;
  index: number;
  isAnimated: boolean;
  x: number;
  y: number;
}) => {
  const progress = useTransform(clock, time =>
    isAnimated ? (getCycleProgress(time / 1000, index * 0.3, 2.7) ?? 0) : 0
  );
  const dotX = useTransform(progress, currentProgress =>
    lerp(x, x * 0.25, getTriangleProgress(currentProgress))
  );
  const dotY = useTransform(progress, currentProgress =>
    lerp(y, y * 0.25, getTriangleProgress(currentProgress))
  );
  const opacity = useTransform(progress, currentProgress =>
    isAnimated ? lerp(0.4, 1, getTriangleProgress(currentProgress)) : 0.72
  );

  return (
    <motion.circle
      cx={dotX}
      cy={dotY}
      r="3.2"
      fill="var(--color-accent-secondary)"
      opacity={opacity}
    />
  );
};

const EnrichGlyph = ({
  clock,
  isAnimated
}: {
  clock: AnimationClock;
  isAnimated: boolean;
}) => (
  <>
    <path
      d="M-27 -17 L0 -32 L27 -17 V17 L0 32 L-27 17 Z"
      fill="var(--color-background-card)"
      stroke="var(--color-border-primary)"
      vectorEffect="non-scaling-stroke"
    />
    <path
      d="M-13 0 H13 M0 -13 V13"
      stroke="var(--color-accent-primary)"
      strokeLinecap="round"
      strokeWidth="1.6"
      vectorEffect="non-scaling-stroke"
    />
    {[
      [-35, -25],
      [36, -23],
      [38, 25]
    ].map(([x, y], index) => (
      <EnrichDot
        key={`${x}-${y}`}
        clock={clock}
        index={index}
        isAnimated={isAnimated}
        x={x}
        y={y}
      />
    ))}
  </>
);

const PublishGlyph = () => (
  <>
    <path
      d="M-34 -31 H34 V31 H-34 Z"
      fill="var(--color-background-card)"
      stroke="var(--color-border-primary)"
      vectorEffect="non-scaling-stroke"
    />
    {[-15, -4, 7, 18].map((y, index) => (
      <rect
        key={y}
        x="-23"
        y={y - 2.5}
        width={index === 3 ? 32 : 44 - index * 4}
        height="5"
        rx="2.5"
        fill="var(--color-accent-success)"
        fillOpacity={0.42 + index * 0.1}
      />
    ))}
  </>
);

const OperationGlyph = ({
  clock,
  isAnimated,
  kind
}: {
  clock: AnimationClock;
  isAnimated: boolean;
  kind: OperationKind;
}) => {
  switch (kind) {
    case 'validate':
      return <ValidateGlyph clock={clock} isAnimated={isAnimated} />;
    case 'transform':
      return <TransformGlyph clock={clock} isAnimated={isAnimated} />;
    case 'enrich':
      return <EnrichGlyph clock={clock} isAnimated={isAnimated} />;
    case 'publish':
      return <PublishGlyph />;
    default:
      return null;
  }
};

const OperationNode = ({
  clock,
  compact,
  hoveredNode,
  id,
  isAnimated,
  kind,
  label,
  onHoverChange,
  point,
  tooltip
}: {
  clock: AnimationClock;
  compact: boolean;
  hoveredNode: HoveredNode;
  id: string;
  isAnimated: boolean;
  kind: OperationKind;
  label: string;
  onHoverChange: (node: HoveredNode) => void;
  point: Point;
  tooltip: string;
}) => {
  const isHovered = hoveredNode === id;

  return (
    <g transform={`translate(${point[0]} ${point[1]})`}>
      <motion.g
        role="img"
        aria-label={`${label}: ${tooltip}`}
        tabIndex={0}
        className="typical-path-node cursor-default outline-none"
        animate={{
          opacity: getNodeOpacity(hoveredNode, id),
          scale: isHovered ? 1.055 : 1
        }}
        transition={{ duration: 0.24, ease: EASE }}
        onFocus={() => onHoverChange(id)}
        onBlur={() => onHoverChange(null)}
        onPointerEnter={() => onHoverChange(id)}
        onPointerLeave={() => onHoverChange(null)}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <title>{`${label}: ${tooltip}`}</title>
        <OperationGlyph clock={clock} kind={kind} isAnimated={isAnimated} />
        <text
          x="0"
          y="53"
          fill="var(--color-text-primary)"
          fontFamily="var(--font-mono)"
          fontSize={compact ? '13' : '10'}
          textAnchor="middle"
        >
          {label}
        </text>
        <AnimatePresence>
          {isHovered && <NodeTooltip label={tooltip} width={88} y={-57} />}
        </AnimatePresence>
      </motion.g>
    </g>
  );
};

const DataProcessingScene = ({
  active,
  clock,
  hoveredNode,
  isAnimated,
  layout,
  onHoverChange,
  route
}: {
  active: boolean;
  clock: AnimationClock;
  hoveredNode: HoveredNode;
  isAnimated: boolean;
  layout: PipelineLayout;
  onHoverChange: (node: HoveredNode) => void;
  route: readonly string[];
}) => {
  const operationIds = ['process-validate', 'transform', 'enrich', 'publish'];
  const operationKinds: readonly OperationKind[] = [
    'validate',
    'transform',
    'enrich',
    'publish'
  ];
  const tooltips = [
    'Rows checked',
    'Shape normalized',
    'Context joined',
    'Output ready'
  ];

  return (
    <g transform={`translate(${layout.width} 0)`}>
      <motion.g
        initial={false}
        animate={{ opacity: active ? 1 : 0.34 }}
        transition={{ duration: 0.4 }}
      >
        <text
          x="18"
          y="20"
          fill="var(--color-text-muted)"
          fontFamily="var(--font-mono)"
          fontSize={layout.compact ? '11' : '8.5'}
          letterSpacing="1.3"
        >
          CONTROLLED PROCESSING ENGINE
        </text>

        {layout.processing.slice(0, -1).map((point, index) => {
          const nextPoint = layout.processing[index + 1];
          const d = `M${point[0] + 34} ${point[1]} C${point[0] + 62} ${point[1]} ${nextPoint[0] - 62} ${nextPoint[1]} ${nextPoint[0] - 34} ${nextPoint[1]}`;

          return (
            <AnimatedConnector
              key={operationIds[index]}
              clock={clock}
              d={d}
              connectedNodes={[operationIds[index], operationIds[index + 1]]}
              hoveredNode={hoveredNode}
              isAnimated={isAnimated && active}
              success={index === 2}
            />
          );
        })}

        {layout.processing.map((point, index) => (
          <OperationNode
            key={operationIds[index]}
            clock={clock}
            compact={layout.compact}
            point={point}
            id={operationIds[index]}
            kind={operationKinds[index]}
            label={route[index] ?? operationKinds[index]}
            tooltip={tooltips[index]}
            hoveredNode={hoveredNode}
            isAnimated={isAnimated && active}
            onHoverChange={onHoverChange}
          />
        ))}

        {isAnimated && active && (
          <>
            <DataPacket
              clock={clock}
              label="Normalized dataset"
              delay={0.35}
              duration={4.15}
              isAnimated={isAnimated && active}
              points={layout.processing.map(point => [point[0], point[1]])}
              success
            />
            <DataPacket
              clock={clock}
              label="Enrichment attributes"
              delay={1.9}
              duration={3.65}
              isAnimated={isAnimated && active}
              points={[
                [layout.processing[1][0], layout.processing[1][1]],
                [layout.processing[2][0], layout.processing[2][1] - 22],
                [layout.processing[2][0], layout.processing[2][1]],
                [layout.processing[3][0], layout.processing[3][1]]
              ]}
              success
            />
          </>
        )}

        <g
          aria-hidden="true"
          transform={`translate(${layout.processing[3][0] + 50} ${layout.processing[3][1]})`}
        >
          <path
            d="M0 0 H22"
            fill="none"
            stroke="var(--color-accent-success)"
            strokeWidth="1.3"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M18 -4 L22 0 L18 4"
            fill="none"
            stroke="var(--color-accent-success)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.3"
            vectorEffect="non-scaling-stroke"
          />
        </g>
      </motion.g>
    </g>
  );
};

const PipelineWorld = ({
  activeStage,
  clock,
  capabilities,
  hoveredNode,
  isAnimated,
  layout,
  onHoverChange,
  reduceMotion
}: {
  activeStage: StageIndex;
  clock: AnimationClock;
  capabilities: readonly [PlatformCapability, PlatformCapability];
  hoveredNode: HoveredNode;
  isAnimated: boolean;
  layout: PipelineLayout;
  onHoverChange: (node: HoveredNode) => void;
  reduceMotion: boolean;
}) => {
  const transferStart = layout.landing[0] + 38;
  const transferEnd = layout.width + layout.processing[0][0] - 34;

  return (
    <svg
      role="group"
      aria-label={`${capabilities[0].title} to ${capabilities[1].title} pipeline`}
      className="block h-full w-full overflow-visible"
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <pattern
          id="typical-path-grid"
          width="22"
          height="22"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M22 0 H0 V22"
            fill="none"
            stroke="var(--color-border-muted)"
            strokeOpacity="0.46"
            strokeWidth="0.7"
            vectorEffect="non-scaling-stroke"
          />
        </pattern>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="url(#typical-path-grid)"
        opacity="0.56"
      />

      <motion.g
        initial={false}
        animate={{ x: activeStage === 0 ? 0 : -layout.width }}
        transition={{
          duration: reduceMotion ? 0.24 : PIPELINE_TIMING.camera,
          ease: EASE
        }}
      >
        <path
          d={`M${transferStart} ${layout.landing[1]} C${transferStart + 48} ${layout.landing[1]} ${transferEnd - 48} ${layout.processing[0][1]} ${transferEnd} ${layout.processing[0][1]}`}
          fill="none"
          stroke="var(--color-border-primary)"
          strokeDasharray="3 6"
          strokeWidth="1.2"
          vectorEffect="non-scaling-stroke"
        />
        <DataIngestionScene
          active={activeStage === 0}
          clock={clock}
          route={capabilities[0].route}
          layout={layout}
          hoveredNode={hoveredNode}
          isAnimated={isAnimated}
          onHoverChange={onHoverChange}
        />
        <DataProcessingScene
          active={activeStage === 1}
          clock={clock}
          route={capabilities[1].route}
          layout={layout}
          hoveredNode={hoveredNode}
          isAnimated={isAnimated}
          onHoverChange={onHoverChange}
        />
        <SharedDataset
          activeStage={activeStage}
          layout={layout}
          reduceMotion={reduceMotion}
        />
      </motion.g>
    </svg>
  );
};

const TypicalPathAnimationInner = ({
  capabilities,
  eyebrow
}: {
  capabilities: readonly [PlatformCapability, PlatformCapability];
  eyebrow: string;
}) => {
  const [activeStage, setActiveStage] = useState<StageIndex>(0);
  const [hoveredNode, setHoveredNode] = useState<HoveredNode>(null);
  const [manualSelection, setManualSelection] = useState(0);
  const [isHoverPaused, setIsHoverPaused] = useState(false);
  const [isUserPaused, setIsUserPaused] = useState(false);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(viewportRef, { amount: 0.2 });
  const shouldReduceMotion = useReducedMotion() ?? false;
  const isCompact = useCompactViewport();
  const layout = isCompact ? COMPACT_LAYOUT : DESKTOP_LAYOUT;
  const isPlaybackPaused = isHoverPaused || isUserPaused;
  const isAnimated = isInView && !shouldReduceMotion;
  const clock = usePausableClock(isAnimated, isPlaybackPaused);
  const tabsId = useId();
  const pointerX = useMotionValue(-240);
  const pointerY = useMotionValue(-240);
  const glowX = useSpring(pointerX, { damping: 30, stiffness: 180 });
  const glowY = useSpring(pointerY, { damping: 30, stiffness: 180 });

  useEffect(() => {
    if (!isInView || shouldReduceMotion || isPlaybackPaused) return;

    const delay =
      manualSelection > 0
        ? PIPELINE_TIMING.manualPause * 1000
        : PIPELINE_TIMING.loop * 1000;
    const timer = window.setTimeout(() => {
      setManualSelection(0);
      setHoveredNode(null);
      setActiveStage(current => (current === 0 ? 1 : 0));
    }, delay);

    return () => window.clearTimeout(timer);
  }, [
    activeStage,
    isInView,
    isPlaybackPaused,
    manualSelection,
    shouldReduceMotion
  ]);

  const selectStage = (stage: StageIndex) => {
    setHoveredNode(null);
    setActiveStage(stage);
    setManualSelection(current => current + 1);
  };

  const togglePlayback = () => {
    setIsUserPaused(current => !current);
  };

  const handlePointerEnter = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') {
      setIsHoverPaused(true);
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch') return;

    const bounds = event.currentTarget.getBoundingClientRect();
    pointerX.set(event.clientX - bounds.left - 90);
    pointerY.set(event.clientY - bounds.top - 90);
  };

  const hidePointerGlow = () => {
    pointerX.set(-240);
    pointerY.set(-240);
  };

  const handlePointerLeave = () => {
    setIsHoverPaused(false);
    hidePointerGlow();
  };

  const handleFocus = () => {
    setIsHoverPaused(true);
  };

  const handleBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsHoverPaused(false);
      setHoveredNode(null);
    }
  };

  const activeCapability = capabilities[activeStage];
  const statusDotOpacity = useTransform(clock, time =>
    isAnimated
      ? lerp(0.4, 1, getTriangleProgress(wrapSeconds(time / 1000, 2.2) / 2.2))
      : 0.8
  );
  const statusLabel = isUserPaused
    ? 'Paused'
    : isHoverPaused
      ? 'Hover paused'
      : activeStage === 0
        ? 'Intake active'
        : 'Processing active';

  return (
    <section
      className="rounded-lg bg-background-card/70 p-4"
      aria-labelledby={`${tabsId}-eyebrow`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div
            id={`${tabsId}-eyebrow`}
            className="font-mono text-xs uppercase tracking-[0.2em] text-text-muted"
          >
            {eyebrow}
          </div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={activeCapability.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -3 }}
              transition={{ duration: shouldReduceMotion ? 0.12 : 0.26 }}
              className="mt-2 max-w-md text-xs leading-5 text-text-secondary"
            >
              {activeCapability.lead}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={togglePlayback}
            aria-label={
              isUserPaused
                ? `Play ${eyebrow.toLowerCase()} animation`
                : `Pause ${eyebrow.toLowerCase()} animation`
            }
            className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-border-secondary bg-background-secondary/80 px-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-text-secondary outline-none transition-colors hover:border-accent-primary/40 hover:text-text-primary focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background-card"
          >
            {isUserPaused ? (
              <Play
                aria-hidden="true"
                className="h-3.5 w-3.5 text-accent-primary"
                strokeWidth="1.8"
              />
            ) : (
              <Pause
                aria-hidden="true"
                className="h-3.5 w-3.5 text-accent-primary"
                strokeWidth="1.8"
              />
            )}
            <span className="hidden sm:inline">
              {isUserPaused ? 'Play' : 'Pause'}
            </span>
          </button>

          <div
            role="tablist"
            aria-label={eyebrow}
            className="flex items-center rounded-lg border border-border-secondary bg-background-secondary/80 p-1"
          >
            {capabilities.map((capability, index) => {
              const stage = index as StageIndex;
              const isActive = activeStage === stage;

              return (
                <button
                  key={capability.id}
                  id={`${tabsId}-tab-${index}`}
                  type="button"
                  role="tab"
                  aria-controls={`${tabsId}-panel`}
                  aria-selected={isActive}
                  aria-label={`Show ${capability.title}`}
                  onClick={() => selectStage(stage)}
                  className={classNames(
                    'relative cursor-pointer rounded-md px-2.5 py-1.5 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background-card',
                    isActive
                      ? 'text-text-primary'
                      : 'text-text-muted hover:text-text-primary'
                  )}
                >
                  {isActive && (
                    <motion.span
                      layoutId={`${tabsId}-active-stage`}
                      aria-hidden="true"
                      className="absolute inset-0 rounded-md bg-background-card shadow-sm shadow-shadow"
                      transition={{
                        duration: shouldReduceMotion ? 0.15 : 0.32,
                        ease: EASE
                      }}
                    />
                  )}
                  <span className="relative flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.1em]">
                    <span
                      className={isActive ? 'text-accent-primary' : undefined}
                    >
                      0{index + 1}
                    </span>
                    <span className="hidden lg:inline">{capability.title}</span>
                    <span className="lg:hidden">{capability.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div
        ref={viewportRef}
        id={`${tabsId}-panel`}
        role="tabpanel"
        aria-labelledby={`${tabsId}-tab-${activeStage}`}
        tabIndex={0}
        onBlurCapture={handleBlur}
        onFocusCapture={handleFocus}
        onPointerEnter={handlePointerEnter}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        className="typical-path-viewport relative mt-3 h-[212px] overflow-hidden rounded-lg border border-border-secondary outline-none focus-visible:ring-2 focus-visible:ring-accent-primary sm:h-[252px]"
      >
        <motion.div
          aria-hidden="true"
          className="typical-path-pointer-glow pointer-events-none absolute left-0 top-0 z-0 h-[180px] w-[180px] rounded-full"
          style={{ x: glowX, y: glowY }}
        />
        <div className="relative z-10 h-full w-full">
          <PipelineWorld
            activeStage={activeStage}
            clock={clock}
            capabilities={capabilities}
            hoveredNode={hoveredNode}
            isAnimated={isAnimated}
            layout={layout}
            onHoverChange={setHoveredNode}
            reduceMotion={shouldReduceMotion}
          />
        </div>
        <div
          aria-hidden="true"
          className="absolute inset-x-4 bottom-2 z-20 flex items-center gap-2"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-text-muted">
            {statusLabel}
          </span>
          <span className="h-px flex-1 bg-border-secondary" />
          <motion.span
            className="h-1.5 w-1.5 rounded-full bg-accent-success"
            style={{ opacity: statusDotOpacity }}
          />
        </div>
      </div>

      <p className="sr-only" aria-live="polite">
        {`Stage ${activeStage + 1}: ${activeCapability.title}. ${activeCapability.lead}`}
      </p>
    </section>
  );
};

export const TypicalPathAnimation = ({
  capabilities,
  eyebrow
}: {
  capabilities: readonly PlatformCapability[];
  eyebrow: string;
}) => {
  const ingestion = capabilities[0];
  const processing = capabilities[1];

  if (!ingestion || !processing) return null;

  return (
    <TypicalPathAnimationInner
      capabilities={[ingestion, processing]}
      eyebrow={eyebrow}
    />
  );
};
